import { ReactNode } from 'react';
import { RxCross2 } from 'react-icons/rx';
import Container from './Container';
import { Box, Modal, SwipeableDrawer } from '@mui/material';
import { useThemeContext } from 'src/context/ThemeProvider';
import classNames from 'classnames';

interface Props {
	isOpen: boolean;
	onClose: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void;
	header: ReactNode;
	body: ReactNode;
	footer?: ReactNode;
	innerFooter?: ReactNode;
	drawerBleeding?: number;
	hideBackdrop?: boolean;
	maxHeight?: string;
	minHeight?: string;
	maxWidth?: string;
	minWidth?: string;
	drawerHeight?: string;
	disableScrollLock?: boolean;
	boxShadow?: string;
	isModal?: boolean;
	modalMaxWidth?: string;
	position?: 'bottom' | 'left' | 'top' | 'right';
	borderRadius?: string;
	backDropColor?: string;
	backgroundColor?: string;
	overflowVisible?: boolean;
	fullWidth?: boolean;
	disablePortal?: boolean;
	closeButton?: boolean;
	notCloseable?: boolean;
	backdropFilter?: string;
}

const DrawerLayout = ({
	header,
	body,
	footer,
	onClose,
	fullWidth,
	innerFooter,
	closeButton = true,
	overflowVisible = false,
}: {
	onClose: () => void;
	header: ReactNode;
	body: ReactNode;
	footer?: ReactNode;
	fullWidth?: boolean;
	innerFooter?: ReactNode;
	overflowVisible?: boolean;
	closeButton?: boolean;
}) => {
	return (
		<>
			<div
				className={classNames('flex h-full flex-col grow-[1]', overflowVisible ? 'overflow-visible' : 'overflow-auto')}
			>
				<div>
					<Container className="w-full !px-0">
						<div>{header}</div>
					</Container>
				</div>

				<div className={classNames('grow', overflowVisible ? 'overflow-y-visible' : 'overflow-y-auto')}>
					<Container
						className={classNames('max-w-[1366px] !px-0', fullWidth ? 'w-full' : 'w-[90%] sm:w-[96%] md:!px-3')}
					>
						<div>{body}</div>
					</Container>

					{innerFooter ? innerFooter : null}
				</div>

				{footer ? <div>{footer}</div> : null}
			</div>
			{closeButton ? (
				<RxCross2
					onClick={onClose}
					className="absolute right-[16px] top-[16px] h-[20px] z-[1] w-[20px] cursor-pointer text-[#9CA3AF]"
				/>
			) : null}
		</>
	);
};

export const Drawer = ({
	isOpen,
	onClose,
	header,
	body,
	footer,
	innerFooter,
	fullWidth,
	drawerBleeding = 0,
	hideBackdrop = false,
	maxHeight = 'calc(100dvh - 100px)',
	minHeight = 'calc(100dvh - 100px)',
	minWidth = '100%',
	maxWidth = '100%',
	drawerHeight = '100%',
	disableScrollLock = false,
	isModal = false,
	position = 'bottom',
	borderRadius = '16px 16px 0px 0px',
	modalMaxWidth = '500px',
	boxShadow = '0px -8px 32px 0px rgba(0, 0, 0, 0.25)',
	backDropColor = 'rgba(0, 0, 0, 0.5)',
	backgroundColor,
	overflowVisible = false,
	disablePortal = false,
	closeButton = true,
	notCloseable = false,
	backdropFilter = 'none',
}: Props) => {
	const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
	const { themeMode } = useThemeContext();

	return (
		<>
			{isModal ? (
				<Modal
					open={isOpen}
					onClose={onClose}
					disablePortal={disablePortal}
					disableEscapeKeyDown={notCloseable}
					hideBackdrop={hideBackdrop}
					aria-labelledby="modal-modal-title"
					disableScrollLock={disableScrollLock}
					aria-describedby="modal-modal-description"
					sx={{
						'&': {
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							zIndex: 50,
						},
						'& .MuiBox-root': {
							outline: 'none',
							overflow: overflowVisible ? 'visible' : 'auto',
						},
						'& > .MuiBackdrop-root': {
							backgroundColor: backDropColor,
							backdropFilter: backdropFilter,
						},
					}}
				>
					<Box
						sx={{
							my: '30px',
							width: '100%',
							bgcolor: backgroundColor ? backgroundColor : themeMode === 'dark' ? '#1F2A37' : '#fff',
							borderRadius: '16px',
							position: 'relative',
							maxWidth: modalMaxWidth,
							display: 'flex',
							overflow: 'auto',
							maxHeight: '90dvh',
							'& > div': {
								width: '100%',
								height: 'auto',
							},
						}}
					>
						<DrawerLayout
							fullWidth={fullWidth}
							header={header}
							footer={footer}
							innerFooter={innerFooter}
							body={body}
							onClose={onClose}
							closeButton={closeButton}
							overflowVisible={overflowVisible}
						/>
					</Box>
				</Modal>
			) : (
				<SwipeableDrawer
					open={isOpen}
					allowSwipeInChildren={true}
					anchor={position}
					disableDiscovery={iOS}
					hideBackdrop={hideBackdrop}
					disableScrollLock={disableScrollLock}
					disableSwipeToOpen={false}
					onClose={onClose}
					onOpen={() => (isOpen = true)}
					swipeAreaWidth={drawerBleeding}
					disableBackdropTransition={!iOS}
					disablePortal={disablePortal}
					ModalProps={{
						keepMounted: true,
					}}
					sx={{
						'&': {
							zIndex: 50,
							pointerEvents: hideBackdrop ? 'none' : 'auto',
						},
						'& > .MuiPaper-root': {
							color: 'inherit',
							borderRadius: borderRadius,
							pointerEvents: hideBackdrop ? 'all' : 'auto',
							background: backgroundColor ? backgroundColor : themeMode === 'dark' ? '#1F2A37' : '#F7F9FB',
							maxHeight: maxHeight,
							minHeight: minHeight,
							maxWidth: maxWidth,
							minWidth: minWidth,
							boxShadow: boxShadow,
							height: drawerHeight,
						},
						'& > .MuiBackdrop-root': {
							backgroundColor: backDropColor,
							backdropFilter: backdropFilter,
						},
					}}
				>
					<DrawerLayout
						fullWidth={fullWidth}
						header={header}
						footer={footer}
						innerFooter={innerFooter}
						body={body}
						onClose={onClose}
						closeButton={closeButton}
						overflowVisible={overflowVisible}
					/>
				</SwipeableDrawer>
			)}
		</>
	);
};
